
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { ShareDocumentDialog } from "./ShareDocumentDialog";
import { SigningRequestDialog } from "./signing/SigningRequestDialog";
import { SigningStatus } from "./signing/SigningStatus";
import { DocumentAnalysisResult } from "./DocumentAnalysisResult";
import { useDocumentAnalysis } from "@/hooks/useDocumentAnalysis";
import { DocumentActions } from "./viewer/DocumentActions";
import { DocumentContent } from "./viewer/DocumentContent";
import { DocumentEditActions } from "./viewer/DocumentEditActions";
import type { Document } from "./DocumentCard";

interface DocumentViewerProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (doc: Document) => void;
  onShare?: (doc: Document) => void;
  onVersionHistory?: (doc: Document) => void;
  title?: string;
}

export function DocumentViewer({
  document,
  isOpen,
  onClose,
  onDownload,
  onShare,
  onVersionHistory,
  title
}: DocumentViewerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSigningDialog, setShowSigningDialog] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { analyzeDocument, isAnalyzing } = useDocumentAnalysis();

  if (!document) return null;

  const handleEdit = () => {
    setEditContent(document.content || "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('documents')
        .update({ 
          content: editContent,
          version: (document.version || 1) + 1
        })
        .eq('id', document.id);
      
      if (error) throw error;
      
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      
      toast({
        title: "Dokument oppdatert",
        description: "Endringene er lagret",
      });
    } catch (error) {
      console.error('Error updating document:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lagring",
        description: "Kunne ikke lagre endringene. Vennligst prøv igjen.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!document.content) {
      toast({
        variant: "destructive",
        title: "Analyse feilet",
        description: "Dokumentet har ikke noe innhold å analysere.",
      });
      return;
    }

    try {
      const result = await analyzeDocument(document.id, document.content);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing document:', error);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-xl font-semibold break-words">
                  {title || document.title}
                </DialogTitle>
                <SigningStatus documentId={document.id} />
              </div>
              <DocumentActions
                document={document}
                onDownload={onDownload}
                onShare={onShare}
                onVersionHistory={onVersionHistory}
                onShowShareDialog={() => setShowShareDialog(true)}
                onShowSigningDialog={() => setShowSigningDialog(true)}
                onAnalyze={handleAnalyze}
                isAnalyzing={isAnalyzing}
              />
            </div>
          </DialogHeader>
          <ScrollArea className="flex-1 p-6 overflow-x-hidden">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {analysisResult && (
                <div className="mb-6">
                  <DocumentAnalysisResult result={analysisResult} />
                </div>
              )}
              <DocumentContent
                content={document.content}
                isEditing={isEditing}
                editContent={editContent}
                onEditContentChange={setEditContent}
                isLoading={isLoading}
              />
            </div>
          </ScrollArea>
          <DocumentEditActions
            isEditing={isEditing}
            isLoading={isLoading}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={() => setIsEditing(false)}
          />
        </DialogContent>
      </Dialog>

      <ShareDocumentDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        document={document}
      />

      <SigningRequestDialog
        isOpen={showSigningDialog}
        onClose={() => setShowSigningDialog(false)}
        document={document}
      />
    </>
  );
}
